import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { getMemToken } from '../auth';

// Create WebSocket Context
const WebSocketContext = createContext(null);

/**
 * WebSocketProvider - Top-level context provider to maintain a single
 * native WebSocket connection across the whole SPA.
 */
export const WebSocketProvider = ({ children }) => {
    const [status, setStatus] = useState('disconnected');
    const [lastMessage, setLastMessage] = useState(null);
    const [error, setError] = useState(null);
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const maxReconnectAttempts = 5;
    const reconnectAttemptRef = useRef(0);

    const connect = useCallback(() => {
        // Read token fresh on every connect attempt (works after silentRefresh too)
        const token = getMemToken();
        if (!token) return;

        // Avoid multiple concurrent connections
        if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
            return;
        }

        setStatus('connecting');

        const host = process.env.REACT_APP_WS_URL || window.location.host;
        const isLocal = host.startsWith('localhost') || host.startsWith('127.');
        const protocol = (!isLocal && window.location.protocol === 'https:') ? 'wss:' : 'ws:';
        const wsUrl = `${protocol}//${host}/ws?token=${token}`;

        try {
            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                setStatus('connected');
                reconnectAttemptRef.current = 0;
                setError(null);
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    setLastMessage(data);

                    if (data.type === 'notification' || data.message) {
                        toast(data.message || 'New update received', {
                            icon: data.type === 'error' ? '❌' : '🔔',
                            style: {
                                borderRadius: '10px',
                                background: '#333',
                                color: '#fff',
                            },
                        });
                    }
                } catch (e) {
                    console.warn('⚠️ WebSocket: Received non-JSON message', event.data);
                }
            };

            ws.onclose = (event) => {
                setStatus('disconnected');

                if (event.code !== 1000 && reconnectAttemptRef.current < maxReconnectAttempts) {
                    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptRef.current), 30000);
                    reconnectTimeoutRef.current = setTimeout(() => {
                        reconnectAttemptRef.current += 1;
                        connect();
                    }, delay);
                }
            };

            ws.onerror = () => {
                setError('Connection failed');
                ws.close();
            };

            wsRef.current = ws;

        } catch (e) {
            console.error('WebSocket initialization error:', e);
            setStatus('disconnected');
        }
    }, []);

    const disconnect = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close(1000, 'User initiated disconnect');
            wsRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
    }, []);

    // Helper to send messages
    const sendMessage = useCallback((type, payload) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type, payload, timestamp: new Date() }));
            return true;
        }
        return false;
    }, []);

    // Initial connection logic
    useEffect(() => {
        connect();
        return () => disconnect();
    }, [connect, disconnect]);

    // Value to be provided to context consumers
    const contextValue = {
        status,
        lastMessage,
        sendMessage,
        error,
        reconnect: connect
    };

    return (
        <WebSocketContext.Provider value={contextValue}>
            {children}
        </WebSocketContext.Provider>
    );
};

// Custom hook to use WebSocket context
export const useWebSocket = () => {
    const context = useContext(WebSocketContext);
    if (!context) {
        throw new Error('useWebSocket must be used within a WebSocketProvider');
    }
    return context;
};
